import { Button, Col, Divider, Input, message, Modal, Row, Select, Space, Table, Upload } from "antd";
import { Download, Upload as UploadIcon, PlusCircle } from "lucide-react";
import UserModal from "../../components/modals/user";
import useUsers from "./useUsers";
import CountryFilter from "../../components/general/countryFilter/countryFilter";
import { USER_TYPES_FILTER } from "../../constants/constants";
import Permission from "../../components/general/permission/permission";
import { exportUsersCSV, importUsersCSV, getUsers } from "../../services/users";
import { useState } from "react";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { Country } from "country-state-city";

/**
 * Users orchestrates the complex UI state handling the DataTables mapping local Filters.
 * Isolates Import/Export operations intercepting Blob files natively into Postgres.
 */
function Users() {
  const {
    country,
    countryOptions,
    data,
    form,
    handleCancel,
    handleFileChange,
    handleOk,
    handleOpenModal,
    handleSearchCountry,
    handleSelectCountry,
    handleTypeFilter,
    loading,
    onTableChange,
    refetch,
    tableColumns,
    tableParams,
    total,
    typeFilter,
    uploads,
    nameFilter,
    emailFilter,
    companyFilter,
    userModal,
    dynamicRoleOptions,
    dynamicUserTypesFilter,
    // Bulletin modal
    bulletinModalOpen,
    setBulletinModalOpen,
    bulletinModalUser,
    bulletinCountry,
    setBulletinCountry,
    bulletinSection,
    setBulletinSection,
    bulletinFile,
    setBulletinFile,
    bulletinFiles,
    bulletinFilesLoading,
    bulletinSending,
    loadBulletinFiles,
    handleSendBulletinToUser,
  } = useUsers();

  const [exportLoading, setExportLoading] = useState(false);
  const [exportExcelLoading, setExportExcelLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importExcelLoading, setImportExcelLoading] = useState(false);

  const handleImportExcel = (file: File) => {
    setImportExcelLoading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json<any>(worksheet);

        let csvContent = "Name,Email,Type,Code,Country,PhonePrefix,Phone,IdentityType,IdentityNumber\n";

        jsonData.forEach((row) => {
          const name = row["Nombre"] || "";
          const email = row["Email"] || "";
          let type = String(row["Tipo de usuario"] || "").toLowerCase();
          if (type === "administrador") type = "admin";
          if (type === "—") type = "";
          
          const code = ""; // Mantenemos Code vacío porque no está en el Excel
          const idType = (row["Tipo de identificación"] === "—" ? "" : String(row["Tipo de identificación"] || "")) || "";
          const idNum = (row["Identificación"] === "—" ? "" : String(row["Identificación"] || "")) || "";

          let countryIso = String(row["País"] || "");
          if (countryIso && countryIso !== "—") {
            const cInfo = Country.getAllCountries().find(c => c.name === countryIso);
            if (cInfo) countryIso = cInfo.isoCode;
          } else {
            countryIso = "";
          }

          let phonePrefix = "";
          let phone = "";
          const phoneRaw = String(row["Teléfono"] || "");
          if (phoneRaw && phoneRaw !== "—") {
             const parts = phoneRaw.trim().split(" ");
             if (parts[0].startsWith("+")) {
               // Transformar de +593 a código ISO (ej. EC) para la base de datos
               const numericCode = parts[0].replace('+', '');
               const pInfo = Country.getAllCountries().find(c => c.phonecode === numericCode);
               if (pInfo) {
                 phonePrefix = pInfo.isoCode;
               } else {
                 phonePrefix = parts[0];
               }
               phone = parts.slice(1).join(" ");
             } else {
               // Si no puso prefijo (+), asumimos el del país que escogió en la columna País
               phonePrefix = countryIso;
               phone = phoneRaw;
             }
          }

          const escapeCSV = (str: string) => `"${str.replace(/"/g, '""')}"`;
          
          csvContent += `${escapeCSV(name)},${escapeCSV(email)},${escapeCSV(type)},${escapeCSV(code)},${escapeCSV(countryIso)},${escapeCSV(phonePrefix)},${escapeCSV(phone)},${escapeCSV(idType)},${escapeCSV(idNum)}\n`;
        });

        const csvBlob = new Blob([csvContent], { type: "text/csv" });
        const csvFile = new File([csvBlob], "imported_excel.csv", { type: "text/csv" });
        
        const result = await importUsersCSV(csvFile);
        message.success(`Se importaron ${result.imported} usuario(s) correctamente`);
        refetch();
      } catch (error: any) {
        const msg = error?.response?.data || "Error procesando el archivo Excel";
        message.error(typeof msg === "string" ? msg : "Error al importar");
      } finally {
        setImportExcelLoading(false);
      }
    };
    reader.onerror = () => {
      message.error("Error al leer el archivo");
      setImportExcelLoading(false);
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const handleExportExcel = async () => {
    try {
      setExportExcelLoading(true);
      const res = await getUsers({
        limit: 10000,
        page: 1,
        name: nameFilter.value,
        email: emailFilter.value,
        companyName: companyFilter.value,
        country: country?.isoCode,
        type: typeFilter,
      });
      
      const usersList = res.data || [];
      
      const excelData = usersList.map((u: any) => {
        const company = u.edges?.belongsToCompany?.[0];
        const sub = company?.edges?.subscription;
        
        let startDate = "—";
        if (sub?.startDate) {
          const d = dayjs(sub.startDate);
          if (d.year() > 1) startDate = d.format('DD/MM/YYYY');
        }
        
        let endDate = "—";
        let daysLeft: number | string = "—";
        if (sub?.endDate) {
          const d = dayjs(sub.endDate);
          if (d.year() > 1) {
            endDate = d.format('DD/MM/YYYY');
            const diff = d.diff(dayjs(), 'days');
            daysLeft = diff < 0 ? "Expirado" : diff;
          }
        }
        
        const plan = sub?.edges?.subscriptionPlan?.name || "—";
        
        let countryName = "—";
        if (u.country) {
          const cInfo = Country.getCountryByCode(u.country);
          countryName = cInfo ? cInfo.name : u.country;
        }

        let phoneCode = "";
        if (u.phonePrefix) {
          const pInfo = Country.getCountryByCode(u.phonePrefix);
          if (pInfo) {
            phoneCode = `${pInfo.phonecode.includes('+') ? '' : '+'}${pInfo.phonecode}`;
          } else {
            phoneCode = u.phonePrefix;
          }
        }

        let phoneStr = "—";
        if (u.phone || phoneCode) {
          phoneStr = `${phoneCode} ${u.phone || ''}`.trim() || "—";
        }

        return {
          "Tipo de usuario": u.type ? u.type.charAt(0).toUpperCase() + u.type.slice(1) : "—",
          "Nombre": u.name || "—",
          "Tipo de identificación": u.identityType || "—",
          "Identificación": u.identityNumber || "—",
          "Email": u.email || "—",
          "País": countryName,
          "Teléfono": phoneStr,
          "Empresa": u.companyName || company?.name || "—",
          "Fecha Inicio": startDate,
          "Fecha Fin": endDate,
          "Días al corte": daysLeft,
          "Plan Activo": plan,
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Usuarios");
      XLSX.writeFile(workbook, `usuarios_${new Date().toISOString().split("T")[0]}.xlsx`);
      
      message.success("Archivo Excel exportado exitosamente");
    } catch (error) {
      message.error("Error al exportar usuarios en Excel");
    } finally {
      setExportExcelLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const blob = await exportUsersCSV({
        name: nameFilter.value,
        email: emailFilter.value,
        companyName: companyFilter.value,
        country: country?.isoCode,
        type: typeFilter,
      });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `usuarios_${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success("Archivo exportado exitosamente");
    } catch (error) {
      message.error("Error al exportar usuarios");
    } finally {
      setExportLoading(false);
    }
  };

  const handleImport = async (file: File) => {
    try {
      setImportLoading(true);
      const result = await importUsersCSV(file);
      message.success(
        `Se importaron ${result.imported} usuario(s) correctamente`
      );
      refetch();
    } catch (error: any) {
      const msg =
        error?.response?.data || "Error al importar usuarios";
      message.error(typeof msg === "string" ? msg : "Error al importar");
    } finally {
      setImportLoading(false);
    }
    return false; // prevent Upload component from auto-uploading
  };

  return (
    <div>
      {/* Header: título + botones de acción */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <h2 style={{ margin: 0 }}>Usuarios</h2>
        <Space size="small" wrap>
          <Permission requiredPermission={"ui:users:create"}>
            <Upload
              accept=".xlsx"
              showUploadList={false}
              beforeUpload={(file) => {
                handleImportExcel(file);
                return false;
              }}
            >
              <Button
                icon={<UploadIcon size={14} />}
                loading={importExcelLoading}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  borderRadius: "8px",
                }}
              >
                Importar XLSX
              </Button>
            </Upload>
          </Permission>
          <Button
            icon={<Download size={14} />}
            onClick={handleExportExcel}
            loading={exportExcelLoading}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              borderRadius: "8px",
            }}
          >
            Exportar XLSX
          </Button>
          <Permission requiredPermission={"ui:users:create"}>
            <Upload
              accept=".csv"
              showUploadList={false}
              beforeUpload={(file) => {
                handleImport(file);
                return false;
              }}
            >
              <Button
                icon={<UploadIcon size={14} />}
                loading={importLoading}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  borderRadius: "8px",
                }}
              >
                Importar CSV
              </Button>
            </Upload>
          </Permission>
          <Button
            icon={<Download size={14} />}
            onClick={handleExport}
            loading={exportLoading}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              borderRadius: "8px",
            }}
          >
            Exportar CSV
          </Button>
          <Permission requiredPermission={"ui:users:create"}>
            <Button
              type="primary"
              id={"add-user"}
              onClick={handleOpenModal}
              icon={<PlusCircle size={14} />}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                borderRadius: "8px",
                background: "#81BC3D",
                borderColor: "#81BC3D",
              }}
            >
              Agregar usuario
            </Button>
          </Permission>
        </Space>
      </div>

      {/* Filtros */}
      <Space direction="horizontal" size="small" style={{ marginBottom: "12px" }} wrap>
        <Input
          placeholder={"Nombre"}
          value={nameFilter.value}
          onChange={nameFilter.onChange}
          style={{ borderRadius: "8px", width: 140 }}
          allowClear
        />
        <Input
          placeholder={"Email"}
          value={emailFilter.value}
          onChange={emailFilter.onChange}
          style={{ borderRadius: "8px", width: 160 }}
          allowClear
        />
        <Input
          placeholder={"Empresa"}
          value={companyFilter.value}
          onChange={companyFilter.onChange}
          style={{ borderRadius: "8px", width: 160 }}
          allowClear
        />
        <CountryFilter
          countryOptions={countryOptions}
          handleSelectCountry={handleSelectCountry}
          handleSearchCountry={handleSearchCountry}
          country={country}
        />
        <Select
          placeholder={"Tipo de usuario"}
          options={dynamicUserTypesFilter}
          onChange={handleTypeFilter}
          value={typeFilter}
          style={{ width: 160 }}
          allowClear
        />
      </Space>

      <Divider style={{ margin: "0 0 12px 0" }} />

      {/* Tabla */}
      <Row gutter={24}>
        <Col span={24}>
          <Table
            loading={loading}
            dataSource={data}
            columns={tableColumns}
            pagination={{
              ...tableParams.pagination,
              total,
            }}
            onChange={onTableChange}
            rowKey={(record) => record.id}
            scroll={{ x: 1100 }}
          />
        </Col>
      </Row>

      <UserModal
        isModalOpen={userModal.isModalOpen}
        handleCancel={handleCancel}
        handleFileChange={handleFileChange}
        handleOk={handleOk}
        form={form}
        loading={userModal.isLoading}
        editing={userModal.editing}
        uploads={uploads}
        url={userModal.current?.url}
        current={userModal.current}
        dynamicRoleOptions={dynamicRoleOptions}
      />

      {/* Modal para enviar boletín a usuario específico */}
      <Modal
        title={`Enviar boletín a ${bulletinModalUser?.name || "usuario"}`}
        open={bulletinModalOpen}
        onCancel={() => setBulletinModalOpen(false)}
        onOk={handleSendBulletinToUser}
        okText="Enviar"
        cancelText="Cancelar"
        confirmLoading={bulletinSending}
        okButtonProps={{ disabled: !bulletinCountry || !bulletinFile }}
      >
        <p style={{ color: "#8c8c8c", marginBottom: 16 }}>
          Se enviará el boletín al email: <strong>{bulletinModalUser?.email}</strong>
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ fontWeight: 500, marginBottom: 4, display: "block" }}>País</label>
            <Select
              placeholder="Selecciona un país"
              value={bulletinCountry || undefined}
              onChange={(val) => {
                setBulletinCountry(val);
                setBulletinFile("");
                loadBulletinFiles(val, bulletinSection);
              }}
              style={{ width: "100%" }}
              options={[
                { label: "🇵🇪 Perú", value: "peru" },
                { label: "🇪🇨 Ecuador", value: "ecuador" },
                { label: "🇵🇦 Panamá", value: "panama" },
                { label: "🇴🇲 Omán", value: "oman" },
              ]}
            />
          </div>

          <div>
            <label style={{ fontWeight: 500, marginBottom: 4, display: "block" }}>Sección</label>
            <Select
              placeholder="Todas las secciones"
              value={bulletinSection || undefined}
              onChange={(val) => {
                setBulletinSection(val || "");
                setBulletinFile("");
                if (bulletinCountry) loadBulletinFiles(bulletinCountry, val || "");
              }}
              allowClear
              style={{ width: "100%" }}
              options={[
                { label: "Boletín 1 (Superficie)", value: "1" },
                { label: "Boletín v3 (Completo)", value: "2" },
              ]}
            />
          </div>

          <div>
            <label style={{ fontWeight: 500, marginBottom: 4, display: "block" }}>Boletín</label>
            <Select
              placeholder={bulletinCountry ? "Selecciona un boletín" : "Primero selecciona un país"}
              value={bulletinFile || undefined}
              onChange={(val) => setBulletinFile(val)}
              loading={bulletinFilesLoading}
              disabled={!bulletinCountry}
              style={{ width: "100%" }}
              options={bulletinFiles}
              showSearch
              filterOption={(input, option) =>
                (option?.label as string || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Users;
